// The Assistant's view model (SCR-310 sessions, SCR-311 conversation; REQ-110, REQ-117, REQ-118).
// Pure and DOM-free (tests/unit/assistant/model.test.ts): where each host keeps the screens, the
// starter requests a principal may ask, the session's state and end reason in words, the refusal
// codes the screens explain, and the transcript merge and polling rule.
//
// No uuid is ever shown to a person (AS-1): a session is named by when and by whom it was started,
// tool rows by the tool's words, and an id the model writes into its own text is masked. Money is
// shown only when the API sends it: `spentMicroUsd` is null for a customer principal, and null is
// never shown as $0 (AS-7).
import type { components } from "../../api/schema";
import type { ApiError } from "../../api/errors";
import type { Host } from "../../host/host";
import type { ScopeMe, ScopePermission } from "../../scope/scope";
import type { ChipTone } from "../../primitives/page";
import { UNKNOWN_USER } from "../../primitives/person-name";

export type AgentSession = components["schemas"]["AgentSession"];
export type AgentSessionState = components["schemas"]["AgentSessionState"];
export type AgentSessionEndReason = components["schemas"]["AgentSessionEndReason"];
export type AgentMessage = components["schemas"]["AgentMessage"];
export type AgentMessageList = components["schemas"]["AgentMessageList"];

/** How often the conversation polls `?after=<seq>` while a turn runs (TASK-664; no event stream). */
export const POLL_INTERVAL_MS = 1500;

/** The API's bound on one operator message (`PostAgentMessageRequest.text`). */
export const MAX_MESSAGE_LENGTH = 4000;

// ---------------------------------------------------------------------------------------------
// Where the screens live

/** The host fields the hrefs read. */
export type AssistantHost = Pick<Host, "kind"> & Partial<Pick<Host, "basePath">>;

const ASSISTANT = "/assistant";

/**
 * The sessions list (NAV-024, NAV-025). The AllCode admin keeps it on the customer page
 * (`/customers/:orgId/assistant`, so `orgId` is required there); the proxy's Admin area mounts it
 * under its base path for the session's own org.
 */
export function assistantHref(host: AssistantHost, orgId?: string): string {
  const base = host.basePath ?? "";
  if (host.kind !== "allcode-admin") return `${base}${ASSISTANT}`;
  if (!orgId) throw new Error("assistantHref: the AllCode admin's Assistant paths name the customer");
  return `${base}/customers/${encodeURIComponent(orgId)}${ASSISTANT}`;
}

/** One conversation (SCR-311). The id travels in the path only; it is never rendered. */
export function assistantSessionHref(host: AssistantHost, sessionId: string, orgId?: string): string {
  return `${assistantHref(host, orgId)}/${encodeURIComponent(sessionId)}`;
}

// ---------------------------------------------------------------------------------------------
// Starter requests (REQ-118): only questions the M0 read tools answer, filtered by the principal

/** A read tool's grant, as `api/internal/app/mgmtagent/toolbox/reads.go` declares it. */
interface ToolGrant {
  /** The org permission an org principal needs (beside the org-admin role), if any. */
  permission?: ScopePermission;
  /** The platform permissions that admit a platform principal; none means platform is refused. */
  platform?: readonly ScopePermission[];
}

export interface Starter {
  /** The read tool that answers it (never shown). */
  tool: string;
  text: string;
}

const REPORTS: ToolGrant = { permission: "reports:read" };
const QUOTAS: ToolGrant = { permission: "reports:read", platform: ["billing:read"] };
const ORG_ADMIN: ToolGrant = {};
const INSTALLATIONS: ToolGrant = { platform: ["installer:run"] };

const STARTERS: readonly (Starter & { grant: ToolGrant })[] = [
  { tool: "usage_by_user", text: "Who used the most in the last 7 days?", grant: REPORTS },
  { tool: "quota_standing", text: "How do our quotas stand right now?", grant: QUOTAS },
  { tool: "org_installation_settings", text: "Which models are turned on by default?", grant: ORG_ADMIN },
  { tool: "list_teams", text: "Which teams are set up, and who is in them?", grant: ORG_ADMIN },
  { tool: "list_installations", text: "Which proxies are installed?", grant: INSTALLATIONS },
];

/** SCR-310 shows four starters. */
export const STARTER_COUNT = 4;

function reaches(me: ScopeMe, grant: ToolGrant): boolean {
  if (me.role === "super-admin") return true;
  if (me.role === "platform") return (grant.platform ?? []).some((p) => me.permissions.includes(p));
  return grant.permission === undefined || me.permissions.includes(grant.permission);
}

/** The starter requests the principal's read tools can answer, at most four; none while signed out. */
export function startersFor(me: ScopeMe | null): Starter[] {
  if (!me) return [];
  return STARTERS.filter((s) => reaches(me, s.grant))
    .slice(0, STARTER_COUNT)
    .map(({ tool, text }) => ({ tool, text }));
}

// ---------------------------------------------------------------------------------------------
// Session state, end reasons and money

export const STATE_LABELS: Readonly<Record<AgentSessionState, string>> = {
  active: "Working",
  waiting: "Waiting for you",
  awaiting_operator: "Paused",
  ended: "Ended",
};

export const STATE_TONES: Readonly<Record<AgentSessionState, ChipTone>> = {
  active: "blue",
  waiting: "green",
  awaiting_operator: "amber",
  ended: "grey",
};

/** Why a session ended, in words (SCR-311's ended banner). */
export const END_REASONS: Readonly<Record<AgentSessionEndReason, string>> = {
  operator: "This conversation was ended.",
  idle: "This conversation ended after 30 minutes without activity.",
  age: "This conversation ended because it was 24 hours old.",
  budget_exhausted: "This conversation ended because its model budget is spent. Start a new conversation to carry on.",
  model_unavailable: "This conversation ended because the model was unavailable. Start a new conversation to try again.",
};

export function endReasonText(reason: AgentSessionEndReason | null): string {
  return reason === null ? "This conversation has ended." : END_REASONS[reason];
}

export function isEnded(state: AgentSessionState): boolean {
  return state === "ended";
}

/** Whether any session carries a spend figure: the column exists only then (null is never $0). */
export function showsSpend(sessions: readonly AgentSession[]): boolean {
  return sessions.some((s) => s.spentMicroUsd !== null);
}

/** Whether any session counts changes (null before M1): the column exists only then. */
export function showsChanges(sessions: readonly AgentSession[]): boolean {
  return sessions.some((s) => s.changesProposed !== null || s.changesApplied !== null);
}

/** "1 of 3 applied", from the counts the API sends; null when it sends none. */
export function changesLabel(session: AgentSession): string | null {
  if (session.changesProposed === null && session.changesApplied === null) return null;
  return `${session.changesApplied ?? 0} of ${session.changesProposed ?? 0} applied`;
}

/**
 * Who started the session. The API names a customer operator by name, else email, and staff as
 * "AllCode support" to a customer reader; one it could not name (null) is the library's "Unknown
 * user", never an id (AS-1).
 */
export function operatorLabel(session: AgentSession): string {
  return session.operatorName?.trim() || UNKNOWN_USER;
}

// ---------------------------------------------------------------------------------------------
// The channel: an `mcp` session is driven by the operator's own Claude Code (M3)

/** How the list and the conversation label an `mcp` session. */
export const CLAUDE_CODE_LABEL = "Claude Code";

/** The Help topic that explains a Claude Code conversation; each shell holds one in its Assistant article. */
export const CLAUDE_CODE_HELP = "assistant.claudeCode";

/**
 * Whether the operator's Claude Code drives the session: the API refuses every message sent to
 * it from a browser, so it has no composer; its proposed changes are still decided here.
 */
export function drivenFromClaudeCode(session: Pick<AgentSession, "channel">): boolean {
  return session.channel === "mcp";
}

/** When, in the reader's locale; the raw value when it is not a date. */
export function when(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

/** A session's accessible name and title: when and by whom, never its id (AS-1). */
export function sessionTitle(session: AgentSession): string {
  return `Conversation started ${when(session.createdAt)} by ${operatorLabel(session)}`;
}

// ---------------------------------------------------------------------------------------------
// Refusals (REQ-118: each code has a Help topic)

export type RefusalCode = "agent_disabled" | "session_terminal" | "agent_daily_budget_exhausted";

/** The Help topic id of each refusal code; each shell holds a topic per id. */
export const REFUSAL_HELP: Readonly<Record<RefusalCode, string>> = {
  agent_disabled: "assistant.agentDisabled",
  session_terminal: "assistant.sessionTerminal",
  agent_daily_budget_exhausted: "assistant.agentDailyBudgetExhausted",
};

export const ASSISTANT_HELP_IDS: readonly string[] = Object.values(REFUSAL_HELP);

export interface Refusal {
  code: RefusalCode;
  title: string;
  text: string;
  help: string;
}

const REFUSAL_COPY: Readonly<Record<RefusalCode, { title: string; text: string }>> = {
  agent_disabled: { title: "The Assistant is turned off", text: "The Assistant is not available for this customer right now." },
  session_terminal: { title: "This conversation has ended", text: "It can be read but takes no more messages. Start a new conversation to carry on." },
  agent_daily_budget_exhausted: { title: "Today's Assistant budget is spent", text: "No new turns can start until the daily budget resets." },
};

/** "in 5 minutes", from a Retry-After in seconds. */
export function retryAfterText(seconds: number): string {
  if (seconds < 60) return `in ${seconds} second${seconds === 1 ? "" : "s"}`;
  if (seconds < 3600) {
    const minutes = Math.ceil(seconds / 60);
    return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
  }
  const hours = Math.round(seconds / 3600);
  return `in about ${hours} hour${hours === 1 ? "" : "s"}`;
}

/** The refusal an error is, with its words and Help topic; null for any other error. */
export function refusalFrom(error: ApiError): Refusal | null {
  const code = error.code as RefusalCode;
  if (!(code in REFUSAL_COPY)) return null;
  const copy = REFUSAL_COPY[code];
  const wait = code === "agent_daily_budget_exhausted" && error.retryAfterSeconds !== null ? ` Try again ${retryAfterText(error.retryAfterSeconds)}.` : "";
  return { code, title: copy.title, text: copy.text + wait, help: REFUSAL_HELP[code] };
}

// ---------------------------------------------------------------------------------------------
// The transcript

const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/** Model text with any uuid it wrote masked: the transcript never shows an id (AS-1). */
export function withoutIds(text: string): string {
  return text.replace(UUID, "[id hidden]");
}

/** The tools' names in words ("read: Usage by person"); an unknown tool keeps its name, spaced. */
export const TOOL_LABELS: Readonly<Record<string, string>> = {
  usage_summary: "Usage summary",
  usage_by_model: "Usage by model",
  usage_by_user: "Usage by person",
  usage_by_team: "Usage by team",
  usage_timeseries: "Usage over time",
  list_quotas: "Quotas",
  quota_standing: "Quota standing",
  org_installation_settings: "Default installation settings",
  installation_settings: "Installation settings",
  list_users: "People",
  list_teams: "Teams",
  list_installations: "Installations",
};

export function toolLabel(tool: string | null): string {
  if (tool === null) return "A tool";
  return TOOL_LABELS[tool] ?? tool.replace(/_/g, " ");
}

/** One rendered transcript row. Tool inputs and results are never rendered: they may hold ids. */
export type TranscriptRow =
  | { kind: "operator" | "assistant"; seq: number; text: string; at: string }
  | { kind: "tool"; seq: number; text: string; failed: boolean }
  | { kind: "event"; seq: number; text: string }
  | { kind: "hidden"; seq: number; text: string };

/** What a reader who is neither the session's operator nor a super-admin sees of a withheld row (B1). */
export const WITHHELD_TEXT = "Hidden: only the person who started this conversation and AllCode administrators can see this.";

/**
 * A failed tool result's refusal in words, by the server code its framed result carries
 * (`toolbox.errorCode` and the loop's `loopCode`, a closed set). The route's own code beside it
 * is customer data and is never shown.
 */
export const TOOL_REFUSALS: Readonly<Record<string, string>> = {
  tool_not_offered: "not available to this sign-in",
  invalid_input: "the request was not valid",
  unknown_installation: "no installation has that name",
  refused: "refused",
  result_too_large: "the answer was too large to read",
  too_many_tool_calls: "too many reads in one turn",
  principal_stale: "the sign-in needs refreshing",
  interrupted: "interrupted",
};

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

/** A row the API withheld from this reader: `withheld: true` on the row, or as its whole body. */
function isWithheld(message: AgentMessage): boolean {
  return ("withheld" in message && message.withheld === true) || record(message.body)?.["withheld"] === true;
}

/** Operator, assistant and event rows are `{text}` (API-311). */
function bodyText(message: AgentMessage): string | null {
  const text = record(message.body)?.["text"];
  return typeof text === "string" ? text : null;
}

/** `{toolUseId, content: {result: {error: <code>, …}, truncated}, isError}`, as the loop frames a refusal. */
function refusalText(message: AgentMessage): string {
  const code = record(record(record(message.body)?.["content"])?.["result"])?.["error"];
  if (typeof code === "string" && code in TOOL_REFUSALS) return TOOL_REFUSALS[code]!;
  return message.outcome === "rejected" ? "refused" : "failed";
}

function readLine(message: AgentMessage): TranscriptRow {
  return { kind: "tool", seq: message.seq, text: `Read: ${toolLabel(message.toolName)}`, failed: false };
}

/**
 * The rows a person sees: operator and assistant text; one "Read:" line per tool use (the
 * `tool_call` row's when the API sends one, else its successful result's); a failed result's
 * refusal; system events that carry text; and a withheld row as hidden, never as an empty
 * bubble. A change event is not a row: the change it announces is a card of its own
 * (`changesToFetch`, change-card.tsx).
 */
export function transcriptRows(messages: readonly AgentMessage[]): TranscriptRow[] {
  const rows: TranscriptRow[] = [];
  /** Tool calls already shown whose result has not arrived, by tool. */
  const shown = new Map<string | null, number>();
  for (const message of messages) {
    if (isWithheld(message)) {
      rows.push({ kind: "hidden", seq: message.seq, text: WITHHELD_TEXT });
      continue;
    }
    const text = bodyText(message);
    switch (message.kind) {
      case "operator":
      case "assistant":
        // A tool-use-only assistant turn carries `text: ""` (the loop always writes the key):
        // its tool line is the "Read:" row, never an empty bubble.
        if (text !== null && text.trim() !== "") rows.push({ kind: message.kind, seq: message.seq, text: withoutIds(text), at: message.createdAt });
        break;
      case "tool_call":
        shown.set(message.toolName, (shown.get(message.toolName) ?? 0) + 1);
        rows.push(readLine(message));
        break;
      case "tool_result": {
        const pending = shown.get(message.toolName) ?? 0;
        if (pending > 0) shown.set(message.toolName, pending - 1);
        if (message.outcome !== null && message.outcome !== "ok") {
          rows.push({ kind: "tool", seq: message.seq, text: `${toolLabel(message.toolName)}: ${refusalText(message)}`, failed: true });
        } else if (pending === 0) {
          rows.push(readLine(message));
        }
        break;
      }
      case "system_event":
        if (text !== null) rows.push({ kind: "event", seq: message.seq, text: withoutIds(text) });
        break;
      case "change_event":
        break;
    }
  }
  return rows;
}

/**
 * How many change events the transcript holds, or the session's proposal count when that is
 * larger: the conversation reads the session's changes (API-312) again whenever it grows, and
 * never while it is zero.
 */
export function changesToFetch(messages: readonly AgentMessage[], session: Pick<AgentSession, "changesProposed">): number {
  const events = messages.filter((m) => m.kind === "change_event").length;
  return Math.max(events, session.changesProposed ?? 0);
}

/** The conversation as polled so far. */
export interface Transcript {
  state: AgentSessionState;
  messages: AgentMessage[];
  /** The greatest `seq` held; the next poll asks `?after=` it. */
  lastSeq: number;
  /** The poll was cut short (100 rows or 256 KiB): ask again at once. */
  hasMore: boolean;
  /** The `seq` of the operator's last message while its turn has produced nothing after it. */
  awaitingReplyTo: number | null;
}

export function emptyTranscript(state: AgentSessionState): Transcript {
  return { state, messages: [], lastSeq: 0, hasMore: false, awaitingReplyTo: null };
}

/** Rows the transcript holds and the poll repeats are kept once, in `seq` order. */
function merged(current: readonly AgentMessage[], incoming: readonly AgentMessage[]): AgentMessage[] {
  const bySeq = new Map(current.map((m) => [m.seq, m]));
  for (const m of incoming) bySeq.set(m.seq, m);
  return [...bySeq.values()].sort((a, b) => a.seq - b.seq);
}

/** A poll's answer folded into the transcript. */
export function applyPoll(transcript: Transcript, list: AgentMessageList): Transcript {
  const messages = merged(transcript.messages, list.items);
  const lastSeq = messages.length === 0 ? transcript.lastSeq : Math.max(transcript.lastSeq, messages[messages.length - 1]!.seq);
  const replied = transcript.awaitingReplyTo !== null && lastSeq > transcript.awaitingReplyTo;
  return { state: list.state, messages, lastSeq, hasMore: list.hasMore, awaitingReplyTo: replied ? null : transcript.awaitingReplyTo };
}

/** The operator's own message, as the post answered it: the turn it starts is awaited. */
export function applySent(transcript: Transcript, message: AgentMessage): Transcript {
  const messages = merged(transcript.messages, [message]);
  return { ...transcript, messages, lastSeq: Math.max(transcript.lastSeq, message.seq), awaitingReplyTo: message.seq };
}

/**
 * Whether to poll again: while a turn runs (`active`), while the last answer was cut short, or
 * after a send until something follows the operator's message. A paused or ended session
 * waits for the operator.
 */
export function shouldPoll(transcript: Transcript): boolean {
  if (transcript.state === "ended" || transcript.state === "awaiting_operator") return transcript.hasMore;
  return transcript.state === "active" || transcript.hasMore || transcript.awaitingReplyTo !== null;
}

/**
 * The transcript as a transport whose turns stream (the proxy's API-586) sees it: there a turn
 * runs only inside its stream, so `active` with no stream running is a turn that died without
 * its closing step, and the session is idle (B2): it takes a message and is not polled.
 */
export function idleWithoutStream(transcript: Transcript): Transcript {
  return transcript.state === "active" ? { ...transcript, state: "waiting" } : transcript;
}

/** Whether a turn is running from the operator's view (the composer is disabled meanwhile). */
export function isThinking(transcript: Transcript): boolean {
  return transcript.state === "active" || (transcript.awaitingReplyTo !== null && !isEnded(transcript.state) && transcript.state !== "awaiting_operator");
}
